// SDK types & ports — canon Phase 4. Core is DOM-free and depends only on ports,
// so it is unit-testable in Node; browser adapters implement the ports.
import type { EventName } from '@fixgarden/shared/eventNames';

export type { EventName };

/** Client-sent event envelope (server adds received_at). Mirrors @fixgarden/shared. */
export interface Envelope {
  schema_version: number;
  event_id: string;
  event_name: EventName;
  event_timestamp: string;
  anonymous_id: string;
  session_id: string;
  quiz_attempt_id?: string;
  lead_id?: string;
  quiz_version?: string;
  interface_version?: string;
  properties: Record<string, unknown>;
  context: Record<string, unknown>;
}

export interface QueuedEvent {
  envelope: Envelope;
  attempts: number;
  nextAttemptAt: number;
}

// ---- ports (implemented by browser adapters / test fakes) ----
export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export interface Clock {
  now(): number;
}

export interface IdSource {
  uuid(): string;
}

export interface Logger {
  debug(...args: unknown[]): void;
  warn(...args: unknown[]): void;
}

/** Schedules `fn` after `ms`; returns a cancel function. */
export interface Scheduler {
  schedule(fn: () => void, ms: number): () => void;
}

export interface Transport {
  /** Resolves true when the batch was delivered. `beacon` = best-effort on unload. */
  send(events: Envelope[], opts?: { beacon?: boolean }): Promise<boolean>;
}

export interface QueueStore {
  loadPending(): Promise<QueuedEvent[]>;
  put(event: QueuedEvent): Promise<void>;
  remove(eventId: string): Promise<void>;
}

/** Control endpoints (session/attempts) — best-effort, not queued like events. */
export interface ApiClient {
  startSession(body: Record<string, unknown>): Promise<void>;
  createAttempt(body: Record<string, unknown>): Promise<void>;
  patchAttempt(id: string, body: Record<string, unknown>): Promise<void>;
}

export interface AnalyticsConfig {
  /** API base, e.g. "http://localhost:3000". */
  endpoint: string;
  schemaVersion?: number;
  quizVersion?: string;
  interfaceVersion?: string;
  batchSize?: number;
  flushIntervalMs?: number;
  maxRetries?: number;
  backoffBaseMs?: number;
  debug?: boolean;
}

export const SDK_DEFAULTS = {
  schemaVersion: 1,
  batchSize: 10,
  flushIntervalMs: 5000,
  maxRetries: 6,
  backoffBaseMs: 1000,
} as const;
